import { BigNumber, ethers } from "ethers";
import { useCallback, useEffect, useState } from "react";
import { IERC20__factory } from "../../codegen";
import {
  getPerpetualDexAddresses,
  getUsdcDecimals,
  isNativeGasQuoteToken,
} from "../../config/chains";
import {
  DepositWithdrawCollaterals,
  getCollateralERC20Addresses,
} from "../../constants/addresses";
import { ChainIdEnum } from "../../enums/chain";
import useWallet from "../wallet/useWallet";
import {
  getReadProviders,
  readVaultTotalBalance,
  readWithFallback,
} from "./onboardingBalanceReads";

const ZERO = BigNumber.from(0);

export const useOnboardingBalances = (active: boolean) => {
  const { account, provider, chainId } = useWallet();
  const [eoaUsdcBalance, setEoaUsdcBalance] = useState<number>();
  const [vaultBalance, setVaultBalance] = useState<number>();
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!active || !account) return;

    setIsLoading(true);
    try {
      const chain = chainId as ChainIdEnum | undefined;
      const readProviders = getReadProviders(provider, chain);
      const decimals = getUsdcDecimals(chain);
      const tokenAddress =
        getPerpetualDexAddresses(chain)?.usdc ||
        getCollateralERC20Addresses(DepositWithdrawCollaterals.Usdc, chain).l1;

      const nativeBalance = isNativeGasQuoteToken(chain)
        ? await readWithFallback(readProviders, (readProvider) =>
            readProvider.getBalance(account)
          )
        : ZERO;
      let erc20Balance = ZERO;
      if (tokenAddress) {
        erc20Balance = await readWithFallback(readProviders, (readProvider) =>
          IERC20__factory.connect(tokenAddress, readProvider).balanceOf(account)
        );
      }

      setEoaUsdcBalance(
        Number(ethers.utils.formatUnits(nativeBalance.add(erc20Balance), decimals))
      );

      // On-chain, not GET /account: account routes need an API key, which a
      // user still onboarding does not have yet.
      const vaultAddress = getPerpetualDexAddresses(chain)?.unifiedVault;
      const vault = await readVaultTotalBalance(
        readProviders,
        vaultAddress,
        account,
        decimals
      );
      if (vault !== undefined) setVaultBalance(vault);
    } catch (error) {
      // Keep the previous state during transient RPC/API failures.
    } finally {
      setIsLoading(false);
    }
  }, [account, active, chainId, provider]);

  useEffect(() => {
    if (!active || !account) {
      setEoaUsdcBalance(undefined);
      setVaultBalance(undefined);
      return undefined;
    }

    refresh();
    const interval = window.setInterval(refresh, 10000);
    return () => window.clearInterval(interval);
  }, [account, active, refresh]);

  return {
    eoaUsdcBalance,
    vaultBalance,
    isLoading,
    refresh,
  };
};
